import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
	Grid,
	Button,
	TextField,
	CircularProgress,
	Box,
	Paper,
	Typography,
	Switch,
	FormControlLabel,
} from '@mui/material';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';

import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import {
	fetchSingleEvent,
	fetchAllEventTypes,
	fetchAllLocations,
	getEventDocumentByReference,
} from 'core/API/events';
import { getJobCodes } from 'core/API/scheduler';
import { fetchAllCustomers } from 'core/API/customers';
import styles from './CreateEvent.module.scss';

const EventDetails: React.FC = () => {
	const history = useHistory();
	const location = useLocation();
	const { workplaceId } = useWorkplacesContext();

	const [documentLinks, setDocumentLinks] = useState<Array<{ ref: string; url: string; label: string }>>([]);
	const [documentsLoading, setDocumentsLoading] = useState(false);

	const locationState = location.state as any;
	const eventFromState = locationState?.event;

	const resolveEventId = (value: any): number | null => {
		if (value === null || value === undefined) return null;
		if (typeof value === 'number') return Number.isFinite(value) ? value : null;
		if (typeof value === 'string') {
			const match = value.match(/(\d+)(?:\/)?$/);
			if (!match) return null;
			const parsed = Number(match[1]);
			return Number.isFinite(parsed) ? parsed : null;
		}
		if (typeof value === 'object') {
			if (value.id !== undefined && value.id !== null) return resolveEventId(value.id);
			if (value['@id']) return resolveEventId(value['@id']);
		}
		return null;
	};

	const eventId =
		resolveEventId(locationState?.eventId) ||
		resolveEventId(eventFromState?.id) ||
		resolveEventId(eventFromState?.['@id']) ||
		null;

	const { data: fetchedEvent, isLoading: isLoadingEvent } = useQuery(
		['event', eventId],
		() => fetchSingleEvent(eventId),
		{
			enabled: !!eventId,
			initialData: eventFromState,
			staleTime: 0,
			refetchOnMount: 'always',
		}
	);

	const eventData = fetchedEvent || eventFromState;

	const extractIdFromIri = (iri: any): number => {
		if (!iri) return 0;
		if (typeof iri === 'number') return Number.isFinite(iri) ? iri : 0;
		if (typeof iri === 'string') {
			const id = parseInt(iri.split('/').pop() || '0', 10);
			return Number.isNaN(id) ? 0 : id;
		}
		if (typeof iri === 'object') {
			if (iri.id !== undefined && iri.id !== null) return extractIdFromIri(iri.id);
			if (iri['@id']) return extractIdFromIri(iri['@id']);
		}
		return 0;
	};

	const eventTypeId = eventData?.type ? extractIdFromIri(eventData.type) : 0;
	const locationId = eventData?.location ? extractIdFromIri(eventData.location) : 0;
	const customerId = eventData?.customer ? extractIdFromIri(eventData.customer) : 0;

	const { data: eventTypes } = useQuery(
		['eventTypes', workplaceId],
		() => fetchAllEventTypes(workplaceId),
		{ enabled: !!workplaceId, staleTime: Infinity }
	);

	const { data: customers } = useQuery(
		['customers'],
		() => fetchAllCustomers(),
		{ staleTime: 0, refetchOnMount: 'always' }
	);

	const { data: jobCodesData } = useQuery(
		['jobCodes'],
		() => getJobCodes(),
		{ staleTime: Infinity }
	);

	const { data: locations } = useQuery(
		['locations', workplaceId],
		() => fetchAllLocations(workplaceId),
		{ enabled: !!workplaceId, staleTime: Infinity }
	);

	const sortedJobCodesData = useMemo(() => {
		if (!jobCodesData) return [];
		return [...jobCodesData].sort((a, b) => (a.jobNumber ?? 999) - (b.jobNumber ?? 999));
	}, [jobCodesData]);

	const selectedEventType = useMemo(() => {
		if (!eventTypes?.member || !eventTypeId) return null;
		return eventTypes.member.find((et: any) => et.id === eventTypeId) || null;
	}, [eventTypes, eventTypeId]);

	const selectedLocation = useMemo(() => {
		if (!locations?.member || !locationId) return null;
		return locations.member.find((loc: any) => loc.id === locationId) || null;
	}, [locations, locationId]);

	// Look up customer from list, then fall back to embedded customer object
	const selectedCustomer = useMemo(() => {
		if (customerId && customers?.member) {
			const found = customers.member.find((c: any) => c.id === customerId);
			if (found) return found;
		}
		if (eventData?.customer && typeof eventData.customer === 'object') {
			return eventData.customer;
		}
		return null;
	}, [customerId, customers?.member, eventData?.customer]);

	const hasCustomer = !!(selectedCustomer || customerId);

	const customerName =
		selectedCustomer?.fullName ||
		[selectedCustomer?.firstName, selectedCustomer?.lastName].filter(Boolean).join(' ') ||
		'';

	// Date / time helpers — no timezone conversion needed for display
	const formatDateForDisplay = (dateStr: string): string => {
		if (!dateStr) return '';
		const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
		if (match) return `${match[2]}/${match[3]}/${match[1]}`;
		return dateStr;
	};

	const formatTimeForDisplay = (timeStr: string): string => {
		if (!timeStr) return '';
		const d = dayjs()
			.startOf('day')
			.add(parseInt(String(timeStr).split(':')[0] || '0', 10), 'hour')
			.add(parseInt(String(timeStr).split(':')[1] || '0', 10), 'minute');
		return d.format('h:mm A');
	};

	const toDocumentRef = (item: any): string => {
		if (!item) return '';
		if (typeof item === 'string') return item.trim();
		if (typeof item === 'object') {
			return String(item['@id'] || item.filePath || item.file_path || item.path || '').trim();
		}
		return '';
	};

	const normalizeDocumentsToList = (raw: any): string[] => {
		if (!raw) return [];
		if (Array.isArray(raw)) return raw.map((x) => toDocumentRef(x)).filter(Boolean);
		if (typeof raw === 'string') {
			return raw.split(',').map((x) => x.trim()).filter(Boolean);
		}
		return [];
	};

	useEffect(() => {
		if (!eventData) return;
		let cancelled = false;
		const uploadsBase = (process.env.REACT_APP_IMAGE_URL || '').replace(/\/$/, '');
		const refs = normalizeDocumentsToList(
			(eventData as any)?.documents || (eventData as any)?.banquet_event_order
		);

		const resolveOne = async (ref: string) => {
			if (/^https?:\/\//i.test(ref)) {
				return { ref, url: ref, label: ref.split('/').pop() || 'Open PDF' };
			}
			if (ref.startsWith('/api/')) {
				try {
					const doc = await getEventDocumentByReference(ref);
					const raw = doc.filePath || doc.file_path || doc.path || '';
					if (!raw) return { ref, url: '', label: 'Preview unavailable' };
					const clean = String(raw).replace(/^\/+/, '').replace(/^uploads\//, '');
					const url = `${uploadsBase}/${clean.startsWith('event_documents/') ? clean : `event_documents/${clean}`}`;
					const label = doc.fileName || doc.file_name || clean.split('/').pop() || 'Open PDF';
					return { ref, url, label };
				} catch {
					return { ref, url: '', label: 'Preview unavailable' };
				}
			}
			if (!ref.includes('/') && ref.toLowerCase().endsWith('.pdf')) {
				const clean = ref.replace(/^\/+/, '');
				return { ref, url: `${uploadsBase}/event_documents/${clean}`, label: clean };
			}
			const clean = ref.replace(/^\/+/, '').replace(/^uploads\//, '');
			return {
				ref,
				url: `${uploadsBase}/${clean.startsWith('event_documents/') ? clean : `event_documents/${clean}`}`,
				label: clean.split('/').pop() || 'Open PDF',
			};
		};

		const resolveAll = async () => {
			if (!refs.length) {
				if (!cancelled) setDocumentLinks([]);
				return;
			}
			setDocumentsLoading(true);
			const out = await Promise.all(refs.map((r) => resolveOne(r)));
			if (!cancelled) {
				setDocumentLinks(out);
				setDocumentsLoading(false);
			}
		};

		resolveAll();
		return () => { cancelled = true; };
	}, [eventData]);

	const eventStaffing: any[] = (eventData as any)?.eventStaffing || (eventData as any)?.event_staffing || [];

	// Shared sx for read-only TextFields — same styling as CreateEvent form inputs
	const roSx = {
		'& .MuiOutlinedInput-root': {
			borderRadius: '6px',
			fontSize: '0.95rem',
			backgroundColor: '#fff',
			color: '#3c5471',
			fontFamily: 'Gotham',
			'& fieldset': { border: '1px solid rgba(0,0,0,0.23)' },
			'&:hover fieldset': { border: '1px solid rgba(0,0,0,0.23)' },
			'&.Mui-focused fieldset': { border: '1px solid rgba(0,0,0,0.23)' },
		},
		'& input': { fontFamily: 'Gotham', color: '#3c5471' },
		'& textarea': { fontFamily: 'Gotham', color: '#3c5471' },
	};

	if (isLoadingEvent && !eventFromState) {
		return (
			<Box sx={{ backgroundColor: '#f4f6fa', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
				<CircularProgress />
			</Box>
		);
	}

	if (!eventData) {
		return (
			<Box sx={{ backgroundColor: '#f4f6fa', minHeight: '100vh', padding: '24px' }}>
				<Paper sx={{ padding: '24px' }}>
					<h2>Event not found</h2>
					<Button variant="outlined" onClick={() => history.goBack()}>Back</Button>
				</Paper>
			</Box>
		);
	}

	return (
		<Box sx={{ backgroundColor: '#f4f6fa', minHeight: '100vh', py: 3 }}>
			<Box maxWidth="1100px" mx="auto">
				{/* Header — Back only, no Save */}
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
					<Button
						onClick={() => history.push('/manager/events')}
						sx={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: '#fff',
							textTransform: 'none',
							fontWeight: 500,
							px: 3,
							py: 0.75,
							'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
						}}
					>
						Back
					</Button>
				</Box>

				<Paper elevation={1}>
					<Box sx={{ p: 3 }}>
						<h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '24px', color: '#333', marginTop: 0 }}>
							View Event
						</h2>

						<Grid container spacing={2}>
							{/* 1. Event Type */}
							<Grid item xs={12} sm={6}>
								<label className={styles.formLabel}>Event Type</label>
								<TextField
									className={styles.formInput}
									fullWidth
									value={selectedEventType?.label || ''}
									InputProps={{ readOnly: true }}
									sx={roSx}
								/>
							</Grid>

							{/* Balance first row */}
							<Grid item xs={12} sm={6} />

							{/* 2. Customer Details */}
							<Grid item xs={12}>
								<Box
									display="flex"
									alignItems="center"
									justifyContent="space-between"
									sx={{ borderBottom: '1px solid #e0e0e0', pb: 0.5 }}
								>
									<Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#3c5471', fontFamily: 'Gotham' }}>
										Customer Details
									</Typography>
									<FormControlLabel
										control={
											<Switch
												checked={hasCustomer}
												disabled
												size="small"
												sx={{
													'& .MuiSwitch-switchBase.Mui-checked': { color: 'rgb(68, 99, 136)' },
													'& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgb(68, 99, 136)' },
													'& .MuiSwitch-switchBase.Mui-disabled.Mui-checked': { color: 'rgb(68, 99, 136)' },
													'& .MuiSwitch-switchBase.Mui-disabled.Mui-checked + .MuiSwitch-track': {
														backgroundColor: 'rgb(68, 99, 136)',
														opacity: 0.7,
													},
												}}
											/>
										}
										label={
											<Typography variant="body2" sx={{ fontFamily: 'Gotham', color: '#3c5471' }}>
												Include customer
											</Typography>
										}
										labelPlacement="start"
										sx={{ mr: 0 }}
									/>
								</Box>
							</Grid>

							{hasCustomer && (
								<>
									<Grid item xs={12} sm={6}>
										<label className={styles.formLabel}>Customer Name</label>
										<TextField
											className={styles.formInput}
											fullWidth
											value={customerName}
											InputProps={{ readOnly: true }}
											sx={roSx}
										/>
									</Grid>

									<Grid item xs={12} sm={6}>
										<label className={styles.formLabel}>Customer Email</label>
										<TextField
											className={styles.formInput}
											fullWidth
											value={selectedCustomer?.email || (eventData as any)?.organizer_email || ''}
											InputProps={{ readOnly: true }}
											sx={roSx}
										/>
									</Grid>

									<Grid item xs={12} sm={6}>
										<label className={styles.formLabel}>Customer Phone Number</label>
										<TextField
											className={styles.formInput}
											fullWidth
											value={selectedCustomer?.phone || (eventData as any)?.organizer_phone_number || ''}
											InputProps={{ readOnly: true }}
											sx={roSx}
										/>
									</Grid>

									<Grid item xs={12} sm={6}>
										<label className={styles.formLabel}>Tax Exempt ID</label>
										<TextField
											className={styles.formInput}
											fullWidth
											value={
												eventData?.taxExemptId ||
												(eventData as any)?.tax_exempt_id ||
												selectedCustomer?.taxExemptId ||
												''
											}
											InputProps={{ readOnly: true }}
											sx={roSx}
										/>
									</Grid>
								</>
							)}

							{/* 3. Event Details */}
							<Grid item xs={12}>
								<Typography
									variant="subtitle1"
									sx={{
										fontWeight: 600,
										color: '#3c5471',
										borderBottom: '1px solid #e0e0e0',
										pb: 0.5,
										fontFamily: 'Gotham',
									}}
								>
									Event Details
								</Typography>
							</Grid>

							<Grid item xs={12} sm={6}>
								<label className={styles.formLabel}>Event Name</label>
								<TextField
									className={styles.formInput}
									fullWidth
									value={eventData?.name || eventData?.title || ''}
									InputProps={{ readOnly: true }}
									sx={roSx}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<label className={styles.formLabel}>Location</label>
								<TextField
									className={styles.formInput}
									fullWidth
									value={selectedLocation?.label || selectedLocation?.name || ''}
									InputProps={{ readOnly: true }}
									sx={roSx}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<label className={styles.formLabel}>Start Date</label>
								<TextField
									className={styles.formInput}
									fullWidth
									value={formatDateForDisplay(eventData?.startDate || eventData?.start || '')}
									InputProps={{ readOnly: true }}
									sx={roSx}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<label className={styles.formLabel}>Start Time</label>
								<TextField
									className={styles.formInput}
									fullWidth
									value={formatTimeForDisplay(eventData?.startTime || '')}
									InputProps={{ readOnly: true }}
									sx={roSx}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<label className={styles.formLabel}>End Date</label>
								<TextField
									className={styles.formInput}
									fullWidth
									value={formatDateForDisplay(eventData?.endDate || eventData?.end || '')}
									InputProps={{ readOnly: true }}
									sx={roSx}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<label className={styles.formLabel}>End Time</label>
								<TextField
									className={styles.formInput}
									fullWidth
									value={formatTimeForDisplay(eventData?.endTime || '')}
									InputProps={{ readOnly: true }}
									sx={roSx}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<label className={styles.formLabel}>Number of Attendees</label>
								<TextField
									className={styles.formInput}
									fullWidth
									value={eventData?.numberOfAttendees || eventData?.numOfAtendees || 1}
									InputProps={{ readOnly: true }}
									sx={roSx}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<label className={styles.formLabel}>Tax Exempt ID</label>
								<TextField
									className={styles.formInput}
									fullWidth
									value={
										eventData?.taxExemptId ||
										(eventData as any)?.tax_exempt_id ||
										selectedCustomer?.taxExemptId ||
										''
									}
									InputProps={{ readOnly: true }}
									sx={roSx}
								/>
							</Grid>

							{/* Job Roles to Support Event */}
							<Grid item xs={12}>
								<label className={styles.formLabel} style={{ display: 'block', marginBottom: '8px' }}>
									Job Roles to Support Event
								</label>
								{eventStaffing.length > 0 ? (
									eventStaffing.map((staff: any, index: number) => {
										const jobCodeId = staff.jobCode ? extractIdFromIri(staff.jobCode) : null;
										const jobCodeItem = sortedJobCodesData.find((jc: any) => jc.id === jobCodeId);
										return (
											<Box key={index} sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
												<Box sx={{ flex: 1 }}>
													<TextField
														label="Job Code"
														className={styles.formInput}
														fullWidth
														value={
															jobCodeItem
																? `${jobCodeItem.jobNumber} - ${jobCodeItem.label}`
																: ''
														}
														InputProps={{ readOnly: true }}
														sx={roSx}
													/>
												</Box>
												<Box sx={{ flex: 1 }}>
													<TextField
														label="Number Of Additional Staff"
														className={styles.formInput}
														fullWidth
														value={staff.numberOfAdditionalStaff || 0}
														InputProps={{ readOnly: true }}
														sx={roSx}
													/>
												</Box>
											</Box>
										);
									})
								) : (
									<Box
										sx={{
											mt: 1,
											border: '1px dashed rgba(0,0,0,0.23)',
											borderRadius: '6px',
											py: 1,
											px: 2,
											color: 'rgba(0,0,0,0.38)',
											fontSize: '0.95rem',
											fontFamily: 'Gotham',
										}}
									>
										No job roles assigned
									</Box>
								)}
							</Grid>

							{/* Banquet Event Order / Documents */}
							<Grid item xs={12}>
								<label className={styles.formLabel} style={{ display: 'block', marginBottom: '8px' }}>
									Banquet Event Order / Documents
								</label>
								{documentsLoading ? (
									<CircularProgress size={20} />
								) : documentLinks.length > 0 ? (
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
										{documentLinks.map((doc, idx) => (
											<Box
												key={`${doc.ref}-${idx}`}
												sx={{
													p: 1.5,
													border: '1px solid #dbe3ee',
													borderRadius: '6px',
													fontFamily: 'Gotham',
												}}
											>
												{doc.url ? (
													<a
														href={doc.url}
														target="_blank"
														rel="noopener noreferrer"
														style={{ color: '#1976D2', textDecoration: 'none' }}
													>
														{doc.label}
													</a>
												) : (
													<Typography variant="body2" color="text.secondary">
														{doc.label}
													</Typography>
												)}
											</Box>
										))}
									</Box>
								) : (
									<Box
										sx={{
											mt: 1,
											border: '1px dashed rgba(0,0,0,0.23)',
											borderRadius: '6px',
											py: 1,
											px: 2,
											color: 'rgba(0,0,0,0.38)',
											fontSize: '0.95rem',
											fontFamily: 'Gotham',
										}}
									>
										No documents attached
									</Box>
								)}
							</Grid>

							{/* Notes */}
							<Grid item xs={12}>
								<label className={styles.formLabel}>Notes</label>
								<TextField
									multiline
									rows={4}
									className={styles.formInput}
									fullWidth
									value={eventData?.notes || eventData?.note || ''}
									InputProps={{ readOnly: true }}
									sx={roSx}
								/>
							</Grid>
						</Grid>
					</Box>
				</Paper>
			</Box>
		</Box>
	);
};

export default EventDetails;
